/**
 * Player OTP session — sessionStorage-backed token store.
 *
 * Holds the session token minted by server-node's /verifyLoginCode handler after a
 * player proves control of their email with a 6-digit code. Modelled directly on
 * roleAssumptionStore: same storage discipline, same snapshot caching, same
 * decoupled-from-React shape so authHeadersFor can read it on every fetch.
 *
 * WHY sessionStorage AND NOT localStorage
 * ---------------------------------------
 * This is a bearer credential. sessionStorage dies with the tab, which bounds how
 * long a token sits on a shared machine after someone walks away. The 60-minute
 * server-side `exp` is the real control; this is defence in depth.
 *
 * There is deliberately no refresh token on this plane (plan §9a S12) — a long-lived
 * renewal credential in browser storage is a bigger prize than a 60-minute token.
 * When it expires the player repeats the OTP.
 */

const STORAGE_KEY = "ct_player_session"
const CHANGE_EVENT = "ct-player-session-change"

const CACHE_COLD = Symbol("cache-cold")
let cachedRaw = CACHE_COLD
let cachedEntry = null

const invalidateCache = () => {
  cachedRaw = CACHE_COLD
  cachedEntry = null
}

const safeConsoleWarn = (...args) => {
  try {
    // eslint-disable-next-line no-console
    console.warn(...args)
  } catch {
    // ignore
  }
}

const isPlausibleEntry = (entry) => {
  if (!entry || typeof entry !== "object") return false
  if (typeof entry.token !== "string" || entry.token.length === 0) return false
  if (typeof entry.expiresAt !== "string" || entry.expiresAt.length === 0) return false
  if (typeof entry.email !== "string" || entry.email.length === 0) return false
  if (typeof entry.userId !== "string" || entry.userId.length === 0) return false
  return true
}

const isExpired = (entry) => {
  const exp = Date.parse(entry?.expiresAt ?? "")
  if (Number.isNaN(exp)) return true
  return Date.now() >= exp
}

const readRaw = () => {
  let raw
  try {
    raw = sessionStorage.getItem(STORAGE_KEY)
  } catch (err) {
    safeConsoleWarn("[playerSessionStore] read failed:", err?.message ?? err)
    return null
  }

  if (!raw) {
    if (cachedRaw !== null) {
      cachedRaw = null
      cachedEntry = null
    }
    return null
  }

  if (raw === cachedRaw) return cachedEntry

  let parsed
  try {
    parsed = JSON.parse(raw)
  } catch (err) {
    safeConsoleWarn("[playerSessionStore] parse failed:", err?.message ?? err)
    cachedRaw = raw
    cachedEntry = null
    return null
  }
  if (!isPlausibleEntry(parsed)) {
    safeConsoleWarn("[playerSessionStore] discarding malformed sessionStorage entry")
    cachedRaw = raw
    cachedEntry = null
    return null
  }
  cachedRaw = raw
  cachedEntry = parsed
  return parsed
}

const writeRaw = (entry) => {
  try {
    if (entry) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entry))
    } else {
      sessionStorage.removeItem(STORAGE_KEY)
    }
    invalidateCache()
    return true
  } catch (err) {
    safeConsoleWarn("[playerSessionStore] write failed:", err?.message ?? err)
    return false
  }
}

const notify = () => {
  try {
    window.dispatchEvent(new Event(CHANGE_EVENT))
  } catch {
    // noop
  }
}

/** Active (non-expired) player session, or null. Pure read. */
export const getPlayerSession = () => {
  const entry = readRaw()
  if (!entry) return null
  if (isExpired(entry)) return null
  return entry
}

/** Including expired, for the expiry-watching hook. Stable reference for useSyncExternalStore. */
export const peekPlayerSession = () => readRaw()

export const getPlayerSessionToken = () => getPlayerSession()?.token ?? null

/**
 * Persist a session. `entry` shape:
 *   { token, expiresAt, email, userId, orgId }
 */
export const setPlayerSession = (entry) => {
  if (!isPlausibleEntry(entry)) {
    throw new Error("Invalid player session: missing token, expiresAt, email or userId")
  }
  if (isExpired(entry)) {
    throw new Error("Refusing to persist an already-expired player session")
  }
  if (!writeRaw(entry)) {
    throw new Error("Unable to persist session — browser storage is not available")
  }
  notify()
}

export const clearPlayerSession = () => {
  writeRaw(null)
  notify()
}

export const subscribePlayerSession = (listener) => {
  const onCustom = () => listener()
  const onStorage = (e) => {
    if (!e || e.key === STORAGE_KEY) listener()
  }
  window.addEventListener(CHANGE_EVENT, onCustom)
  window.addEventListener("storage", onStorage)
  return () => {
    window.removeEventListener(CHANGE_EVENT, onCustom)
    window.removeEventListener("storage", onStorage)
  }
}
